import { useResearchTutorialActions, useResearchTutorialStore } from './researchTutorialContext';

import { TourTooltipCard } from '@/components/TourTooltipCard';
import type { SpotlightStep } from '@/components/spotlight/SpotlightTour';
import {
  RESEARCH_TUTORIAL_SEGMENTS,
  segmentForTourKey,
  zoneKey,
} from '@/constants/researchTutorial';

/**
 * The card each step of the Research tutorial shows beside its highlight.
 *
 * Skip is offered on every step but the very last, and leaving counts as having
 * seen the tutorial. A step with an action replaces Next with it, and a step
 * waiting on the reader, such as the terms checkbox, holds its button disabled
 * until they have done it.
 */
export function ResearchTourTooltip({ step }: { step: SpotlightStep }) {
  const { advance, skip } = useResearchTutorialActions();

  const segment = segmentForTourKey(step.tourKey);

  const { zone, isFirstStep, isLastStep } = step;

  const canAdvance = useResearchTutorialStore((state) =>
    segment ? (state.zones[zoneKey(segment, zone)]?.canAdvance ?? true) : true
  );

  const definition = segment ? RESEARCH_TUTORIAL_SEGMENTS[segment] : undefined;

  const text = definition?.steps[zone]?.text;

  if (!segment || !definition || !text) {
    return null;
  }

  const isFinalStep = isLastStep && definition.next === 'done';

  const primaryLabel = definition.steps[zone]?.actionLabel ?? (isFinalStep ? 'Done' : 'Next');

  return (
    <TourTooltipCard
      text={text}
      textRef={step.focusRef}
      primaryLabel={primaryLabel}
      canAdvance={canAdvance}
      onPrimary={() =>
        advance({ segment, zone, isLastStep, handleNext: step.next, handleStop: step.stop })
      }
      onSkip={isFinalStep ? undefined : () => skip(segment, zone, step.stop)}
      onBack={isFirstStep ? undefined : step.prev}
    />
  );
}
